import { useState } from 'react';
import type {
  SpamAnalysis,
  SpamFeed,
  SpamProtections,
  SpamPolicy,
  SpamPolicyVersion,
  SpamRule,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { href } from './router';
import { packsIn, PRESETS, RULE_PACKS, withPack, withPreset } from './spamPacks';
import { ActionButton, Badge, Loaded, useLoad, when } from './ui';

const EMPTY: SpamPolicy = {
  allow_senders: [],
  block_senders: [],
  allow_ips: [],
  block_ips: [],
  add_header_score: null,
  reject_score: null,
  rules: [],
};

const FIELDS = ['subject', 'from', 'to', 'body', 'url_domain', 'attachment_ext', 'header:'];

const lines = (list: readonly string[]) => list.join('\n');
const unlines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

/**
 * The policy editor, for the platform (`base` = /platform/spam-policy) or one
 * tenant (`/tenants/<slug>/spam-policy`). Nothing applies until Save; every
 * save is a new version that can be restored.
 */
export function PolicyEditor({ base, scope }: { base: string; scope: string }) {
  const [load, reload] = useLoad(
    (signal) => api<SpamPolicyVersion>(base, { signal }),
    `policy:${base}`,
  );
  const [versions, reloadVersions] = useLoad(
    (signal) => api<{ versions: SpamPolicyVersion[] }>(`${base}/versions`, { signal }),
    `versions:${base}`,
  );
  const [draft, setDraft] = useState<SpamPolicy | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const current = draft ?? (load.state === 'ready' ? { ...EMPTY, ...load.data.policy } : null);
  const edit = (change: Partial<SpamPolicy>) => current && setDraft({ ...current, ...change });
  const setRule = (i: number, change: Partial<SpamRule>) =>
    current && edit({ rules: current.rules.map((r, j) => (i === j ? { ...r, ...change } : r)) });

  return (
    <Loaded load={load}>
      {(loaded) =>
        current && (
          <>
            <p className="adm-muted">
              {scope} · version {loaded.version}
              {loaded.created_by
                ? ` by ${loaded.created_by}, ${when(loaded.created_at)}`
                : ' (none saved yet)'}
            </p>

            <section className="adm-panel">
              <h3>Senders and networks</h3>
              <div className="adm-lists">
                {(
                  [
                    ['allow_senders', 'Always accept from', 'boss@partner.com, @partner.com'],
                    ['block_senders', 'Always reject from', 'spammer@example.com, @bad.example'],
                    ['allow_ips', 'Always accept from IPs', '203.0.113.0/24'],
                    ['block_ips', 'Always reject from IPs', '198.51.100.7'],
                  ] as const
                ).map(([key, label, hint]) => (
                  <label key={key}>
                    {label}
                    <textarea
                      rows={5}
                      value={lines(current[key])}
                      onChange={(e) => edit({ [key]: unlines(e.target.value) })}
                      placeholder={hint}
                    />
                  </label>
                ))}
              </div>
              <p className="adm-muted adm-small">
                One per line: an address, <code>@domain</code> or domain; an IP or CIDR. Allow lists
                outweigh any spam score but never a virus.
              </p>
            </section>

            <section className="adm-panel">
              <h3>Thresholds</h3>
              <div className="adm-inline">
                <label>
                  Junk at score
                  <input
                    type="number"
                    step="0.5"
                    value={current.add_header_score ?? ''}
                    placeholder="default 6"
                    onChange={(e) =>
                      edit({
                        add_header_score: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </label>
                <label>
                  Reject at score
                  <input
                    type="number"
                    step="0.5"
                    value={current.reject_score ?? ''}
                    placeholder="default 12"
                    onChange={(e) =>
                      edit({ reject_score: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </label>
              </div>
            </section>

            <section className="adm-panel">
              <h3>Starting points and rule packs</h3>
              <p className="adm-muted adm-small">
                A starting point sets the scores and the packs below; your sender lists and own
                rules stay. Nothing changes until you save, and every save can be rolled back.
              </p>
              <div className="adm-inline" role="group" aria-label="Starting points">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="btn ghost"
                    title={preset.description}
                    onClick={() => {
                      setDraft(withPreset(current, preset));
                      setSaved(`${preset.name} applied to the draft. Review it, then save.`);
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
              <ul className="adm-catalogue" aria-label="Rule packs">
                {RULE_PACKS.map((pack) => {
                  const on = packsIn(current).includes(pack.id);
                  return (
                    <li key={pack.id}>
                      <label className="adm-check">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => setDraft(withPack(current, pack.id, e.target.checked))}
                        />{' '}
                        <strong>{pack.name}</strong>
                        <p className="adm-note">{pack.description}</p>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="adm-panel">
              <h3>Rules</h3>
              {current.rules.every((r) => r.pack) && (
                <p className="adm-muted">
                  No rules of your own. Add one, or draw one from a sample message below.
                </p>
              )}
              {current.rules.map((rule, i) =>
                rule.pack ? null : (
                  <div className="adm-rule" key={i}>
                    <input
                      value={rule.name}
                      onChange={(e) => setRule(i, { name: e.target.value })}
                      aria-label="Rule name"
                      placeholder="name"
                    />
                    <select
                      value={rule.field.startsWith('header:') ? 'header:' : rule.field}
                      onChange={(e) => setRule(i, { field: e.target.value })}
                      aria-label="Field"
                    >
                      {FIELDS.map((f) => (
                        <option key={f} value={f}>
                          {f === 'header:' ? 'header…' : f.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                    {rule.field.startsWith('header:') && (
                      <input
                        value={rule.field.slice(7)}
                        onChange={(e) => setRule(i, { field: `header:${e.target.value}` })}
                        aria-label="Header name"
                        placeholder="X-Mailer"
                      />
                    )}
                    <select
                      value={rule.match}
                      onChange={(e) => setRule(i, { match: e.target.value as SpamRule['match'] })}
                      aria-label="Match"
                    >
                      <option value="contains">contains</option>
                      <option value="equals">equals</option>
                      <option value="regex">matches regex</option>
                    </select>
                    <input
                      value={rule.value}
                      onChange={(e) => setRule(i, { value: e.target.value })}
                      aria-label="Value"
                      placeholder="value"
                    />
                    <select
                      value={rule.action}
                      onChange={(e) => setRule(i, { action: e.target.value as SpamRule['action'] })}
                      aria-label="Action"
                    >
                      <option value="score">add score</option>
                      <option value="junk">send to Junk</option>
                      <option value="reject">reject</option>
                      <option value="allow">always accept</option>
                    </select>
                    {rule.action === 'score' && (
                      <input
                        type="number"
                        step="0.5"
                        value={rule.score ?? ''}
                        onChange={(e) =>
                          setRule(i, {
                            score: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        aria-label="Score"
                      />
                    )}
                    <select
                      value={rule.mode}
                      onChange={(e) => setRule(i, { mode: e.target.value as SpamRule['mode'] })}
                      aria-label="Mode"
                    >
                      <option value="observe">observe only</option>
                      <option value="enforce">enforce</option>
                    </select>
                    <button
                      type="button"
                      className="adm-copy"
                      onClick={() => edit({ rules: current.rules.filter((_, j) => j !== i) })}
                    >
                      Remove
                    </button>
                  </div>
                ),
              )}
              <div className="adm-inline">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() =>
                    edit({
                      rules: [
                        ...current.rules,
                        {
                          name: '',
                          field: 'subject',
                          match: 'contains',
                          value: '',
                          action: 'junk',
                          mode: 'observe',
                        },
                      ],
                    })
                  }
                >
                  Add rule
                </button>
              </div>
              <p className="adm-muted adm-small">
                A rule in <b>observe only</b> changes nothing; it is recorded on each message it
                would have matched. Test it with a sample below, then switch it to enforce.
              </p>
            </section>

            <div className="adm-inline">
              <ActionButton
                disabled={draft === null}
                run={async () => {
                  const result = await api<{ version: number; published: boolean }>(base, {
                    method: 'PUT',
                    body: current,
                  });
                  setSaved(
                    `Saved as version ${result.version}${result.published ? ' and applied' : ' — applying within a minute'}.`,
                  );
                  setDraft(null);
                  reload();
                  reloadVersions();
                }}
              >
                Save policy
              </ActionButton>
              {draft && (
                <button type="button" className="btn ghost" onClick={() => setDraft(null)}>
                  Discard changes
                </button>
              )}
              {saved && <span className="adm-ok">{saved}</span>}
            </div>

            <Analyzer base={base} onApply={(change) => current && setDraft(change(current))} />

            <section className="adm-panel">
              <h3>History</h3>
              <Loaded load={versions}>
                {({ versions: list }) =>
                  list.length === 0 ? (
                    <p className="adm-muted">No versions yet.</p>
                  ) : (
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Saved</th>
                          <th>By</th>
                          <th>Contents</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((v) => (
                          <tr key={v.version}>
                            <td className="adm-mono">{v.version}</td>
                            <td>{when(v.created_at)}</td>
                            <td>{v.created_by}</td>
                            <td className="adm-small">
                              {v.policy.rules.length} rules · {v.policy.block_senders.length}{' '}
                              blocked · {v.policy.allow_senders.length} allowed
                            </td>
                            <td>
                              {v.version !== loaded.version && (
                                <ActionButton
                                  tone="ghost"
                                  run={async () => {
                                    await api(`${base}/rollback`, {
                                      method: 'POST',
                                      body: { version: v.version },
                                    });
                                    setDraft(null);
                                    reload();
                                    reloadVersions();
                                  }}
                                >
                                  Restore
                                </ActionButton>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </Loaded>
            </section>
          </>
        )
      }
    </Loaded>
  );
}

function Analyzer({
  base,
  onApply,
}: {
  base: string;
  onApply: (change: (p: SpamPolicy) => SpamPolicy) => void;
}) {
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<SpamAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <section className="adm-panel">
      <h3>Write rules from a message</h3>
      <p className="adm-muted adm-small">
        Paste a message — headers and body, as &ldquo;show original&rdquo; gives it. It is scored by
        the live filter with the saved policy, and rules are suggested from it. Suggested rules
        start in observe mode; apply them, save, and analyse again to see them match.
      </p>
      <textarea
        rows={8}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="adm-sample"
        aria-label="Sample message"
      />
      <div className="adm-inline">
        <button
          type="button"
          className="btn ghost"
          disabled={busy || !message.trim()}
          onClick={() => {
            setBusy(true);
            setError(null);
            api<SpamAnalysis>(`${base}/analyze`, { method: 'POST', body: { message } })
              .then(setResult)
              .catch((e: unknown) => setError(describeError(e)))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Scoring…' : 'Analyse'}
        </button>
        {error && <span className="adm-error">{error}</span>}
      </div>
      {result && (
        <div className="adm-analysis">
          <p>
            <Badge
              tone={
                result.action === 'reject' ? 'crit' : result.action === 'no action' ? 'ok' : 'warn'
              }
            >
              {result.action}
            </Badge>{' '}
            score <b>{result.score.toFixed(2)}</b> / {result.required_score}
          </p>
          <table className="adm-table">
            <tbody>
              {result.symbols.slice(0, 15).map((s) => (
                <tr key={s.name}>
                  <td className="adm-mono">{s.name}</td>
                  <td className="adm-mono">{s.score.toFixed(2)}</td>
                  <td className="adm-small adm-muted">{s.options.join(', ') || s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4>Suggestions</h4>
          <ul className="adm-suggest">
            {result.suggestions.map((s) => (
              <li key={s.label}>
                <span>{s.label}</span>
                <button
                  type="button"
                  className="adm-copy"
                  onClick={() =>
                    onApply((p) => {
                      if (s.kind === 'block_sender' && s.value) {
                        return { ...p, block_senders: [...p.block_senders, s.value] };
                      }
                      if (s.kind === 'allow_sender' && s.value) {
                        return { ...p, allow_senders: [...p.allow_senders, s.value] };
                      }
                      return s.rule ? { ...p, rules: [...p.rules, s.rule] } : p;
                    })
                  }
                >
                  Apply
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ─── Platform page: policy and feeds ─────────────────────────────────────────

/**
 * Feeds the platform can add in one click, with each provider's terms: a
 * commercial mail platform may use only what its terms allow.
 * `{ABUSECH_AUTH_KEY}` is filled in by the API from node.env when it fetches.
 */
const CATALOGUE = [
  {
    name: 'spamhaus-drop',
    url: 'https://www.spamhaus.org/drop/drop.txt',
    kind: 'ip',
    action: 'reject',
    score: 0,
    label: 'Spamhaus DROP',
    what: 'Hijacked and criminal IPv4 networks',
    terms: 'Free for any use, commercial included; credit Spamhaus.',
    needsAbusech: false,
  },
  {
    name: 'spamhaus-drop-v6',
    url: 'https://www.spamhaus.org/drop/drop_v6.json',
    kind: 'ip',
    action: 'reject',
    score: 0,
    label: 'Spamhaus DROPv6',
    what: 'The same, for IPv6',
    terms: 'Free for any use, commercial included; credit Spamhaus.',
    needsAbusech: false,
  },
  {
    name: 'urlhaus',
    url: 'https://urlhaus-api.abuse.ch/v2/files/exports/{ABUSECH_AUTH_KEY}/hostfile.txt',
    kind: 'domain',
    action: 'score',
    score: 8,
    label: 'abuse.ch URLhaus',
    what: 'Hosts serving malware',
    terms:
      'Needs a free Auth-Key from auth.abuse.ch (ABUSECH_AUTH_KEY in node.env). abuse.ch says commercial use may need a paid subscription.',
    needsAbusech: true,
  },
  {
    name: 'openphish',
    url: 'https://openphish.com/feed.txt',
    kind: 'url',
    action: 'score',
    score: 8,
    label: 'OpenPhish community feed',
    what: 'Live phishing pages',
    terms: "Free community feed; check OpenPhish's terms before relying on it commercially.",
    needsAbusech: false,
  },
] as const;

export function SpamPage() {
  return (
    <>
      <div className="adm-head">
        <h2>Spam policy</h2>
      </div>
      <p className="adm-muted">
        The platform policy applies to all inbound mail, together with each tenant&apos;s own: a
        block list (either one) wins over an allow list, and the Junk and reject scores a tenant
        leaves blank are these. Tenants set theirs under{' '}
        <a href={href({ page: 'tenants' })}>Tenants</a> → a tenant → Spam policy.
      </p>
      <PolicyEditor base="/platform/spam-policy" scope="Platform policy" />
      <Protections />
      <Feeds />
    </>
  );
}

function Feeds() {
  const [load, reload] = useLoad(
    (signal) => api<{ feeds: SpamFeed[] }>('/platform/spam-feeds', { signal }),
    'feeds',
  );
  const [error, setError] = useState<string | null>(null);
  const add = (feed: {
    name: string;
    url: string;
    kind: string;
    action: string;
    score: number;
  }) => {
    setError(null);
    api('/platform/spam-feeds', { method: 'POST', body: feed })
      .then(() => setTimeout(reload, 500))
      .catch((e: unknown) => setError(describeError(e)));
  };

  return (
    <section className="adm-panel">
      <h3>Upstream feeds</h3>
      <p className="adm-muted adm-small">
        Lists of bad domains, links and networks, fetched every hour and checked on every inbound
        message. Add one from the catalogue below, or any list of your own.
      </p>
      <Loaded load={load}>
        {({ feeds }) => (
          <>
            {feeds.length > 0 && (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Feed</th>
                    <th>Effect</th>
                    <th>Last fetch</th>
                    <th>Entries</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {feeds.map((f) => (
                    <tr key={f.id}>
                      <td>
                        <strong>{f.name}</strong> <Badge tone="neutral">{f.kind}</Badge>
                        <div className="adm-mono adm-small adm-muted">{f.url}</div>
                      </td>
                      <td>
                        <FeedEffect feed={f} onSaved={reload} />
                      </td>
                      <td>
                        {f.last_status ? (
                          <Badge tone={f.last_status.startsWith('ok') ? 'ok' : 'crit'}>
                            {f.last_status}
                          </Badge>
                        ) : (
                          'pending'
                        )}
                        <div className="adm-small adm-muted">{when(f.last_fetched_at)}</div>
                      </td>
                      <td>{f.entries.toLocaleString()}</td>
                      <td className="adm-actions">
                        <ActionButton
                          tone="ghost"
                          run={async () => {
                            await api(`/platform/spam-feeds/${f.id}/enabled`, {
                              method: 'POST',
                              body: { enabled: !f.enabled },
                            });
                            reload();
                          }}
                        >
                          {f.enabled ? 'Disable' : 'Enable'}
                        </ActionButton>
                        <ActionButton
                          tone="ghost"
                          run={async () => {
                            await api(`/platform/spam-feeds/${f.id}/delete`, { method: 'POST' });
                            reload();
                          }}
                        >
                          Remove
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <FeedCatalogue
              have={feeds.map((f) => f.name)}
              onAdd={add}
              onRefresh={async () => {
                await api('/platform/spam-feeds/refresh', { method: 'POST' });
                setTimeout(reload, 3000);
              }}
            />
            {error && <p className="adm-error">{error}</p>}
            <CustomFeed onAdd={add} />
          </>
        )}
      </Loaded>
    </section>
  );
}

function CustomFeed({
  onAdd,
}: {
  onAdd: (f: { name: string; url: string; kind: string; action: string; score: number }) => void;
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState('domain');
  const [action, setAction] = useState<'score' | 'reject'>('score');
  const [score, setScore] = useState(6);
  return (
    <form
      className="adm-inline"
      onSubmit={(e) => {
        e.preventDefault();
        onAdd({ name, url, kind, action, score });
      }}
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value.toLowerCase())}
        placeholder="feed name"
        aria-label="Feed name"
        required
      />
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://… list URL"
        aria-label="Feed URL"
        required
      />
      <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Feed kind">
        <option value="domain">domains</option>
        <option value="url">URLs</option>
        <option value="ip">IPs / CIDRs</option>
      </select>
      <select
        value={action}
        onChange={(e) => setAction(e.target.value as 'score' | 'reject')}
        aria-label="When listed"
      >
        <option value="score">add a score</option>
        <option value="reject">reject</option>
      </select>
      {action === 'score' && (
        <input
          type="number"
          step="0.5"
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          aria-label="Score"
        />
      )}
      <button type="submit" className="btn ghost">
        Add feed
      </button>
    </form>
  );
}

/** What a listing on the feed does, changeable in place. */
function FeedEffect({ feed, onSaved }: { feed: SpamFeed; onSaved: () => void }) {
  const [action, setAction] = useState(feed.action);
  const [score, setScore] = useState(feed.score);
  const changed = action !== feed.action || (action === 'score' && score !== feed.score);
  return (
    <div className="adm-inline">
      <select
        value={action}
        onChange={(e) => setAction(e.target.value as SpamFeed['action'])}
        aria-label={`${feed.name}: when listed`}
      >
        <option value="score">score</option>
        <option value="reject">reject</option>
      </select>
      {action === 'score' && (
        <input
          type="number"
          step="0.5"
          min={-50}
          max={50}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          aria-label={`${feed.name}: score`}
          style={{ width: '5.5em' }}
        />
      )}
      {changed && (
        <ActionButton
          tone="ghost"
          run={async () => {
            await api(`/platform/spam-feeds/${feed.id}/effect`, {
              method: 'POST',
              body: { action, score },
            });
            onSaved();
          }}
        >
          Save
        </ActionButton>
      )}
    </div>
  );
}

export function TenantSpamPage({ slug }: { slug: string }) {
  return (
    <>
      <div className="adm-head">
        <a href={href({ page: 'tenant', slug })} className="adm-back">
          ← {slug}
        </a>
      </div>
      <div className="adm-head">
        <h2>Spam policy — {slug}</h2>
      </div>
      <PolicyEditor
        base={`/tenants/${encodeURIComponent(slug)}/spam-policy`}
        scope="Tenant policy (after the platform's)"
      />
    </>
  );
}

function FeedCatalogue({
  have,
  onAdd,
  onRefresh,
}: {
  have: readonly string[];
  onAdd: (f: { name: string; url: string; kind: string; action: string; score: number }) => void;
  onRefresh: () => Promise<void>;
}) {
  const [load] = useLoad(
    (signal) => api<SpamProtections>('/platform/spam-protections', { signal }),
    'protections:catalogue',
  );
  const abusech = load.state === 'ready' && load.data.abusech_key;
  return (
    <>
      <h4>Catalogue</h4>
      <ul className="adm-catalogue">
        {CATALOGUE.map((c) => {
          const added = have.includes(c.name);
          const blocked = c.needsAbusech && !abusech;
          return (
            <li key={c.name}>
              <div>
                <strong>{c.label}</strong>{' '}
                <span className="adm-muted">
                  — {c.what}; {c.action === 'reject' ? 'rejects' : `+${c.score}`}
                </span>
                <p className="adm-note">{c.terms}</p>
              </div>
              {added ? (
                <Badge tone="ok">Added</Badge>
              ) : (
                <button
                  type="button"
                  className="btn ghost"
                  disabled={blocked}
                  title={blocked ? 'Set ABUSECH_AUTH_KEY in node.env first' : c.url}
                  onClick={() => onAdd(c)}
                >
                  Add
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div className="adm-inline">
        <ActionButton tone="ghost" run={onRefresh}>
          Fetch all now
        </ActionButton>
      </div>
    </>
  );
}

/** What every Rspamd checks besides these feeds, as each last reported it. */
function Protections() {
  const [load] = useLoad(
    (signal) => api<SpamProtections>('/platform/spam-protections', { signal }),
    'protections',
  );
  const on = (yes: boolean) => <Badge tone={yes ? 'ok' : 'neutral'}>{yes ? 'On' : 'Off'}</Badge>;
  return (
    <section className="adm-panel">
      <h3>Built-in protection</h3>
      <p className="adm-muted adm-small">
        Checked on every message by each mail server&apos;s filter, besides the policy and feeds on
        this page.
      </p>
      <Loaded load={load}>
        {({ nodes }) =>
          nodes.length === 0 ? (
            <p className="adm-muted">
              No filter has reported yet. Each one reports when it starts.
            </p>
          ) : (
            <>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Filter</th>
                    <th>Spamhaus</th>
                    <th>DNS blocklists</th>
                    <th>PhishTank</th>
                    <th>Shared spam hashes</th>
                  </tr>
                </thead>
                <tbody>
                  {nodes.map((n) => (
                    <tr key={n.host}>
                      <td>
                        <span className="adm-mono">{n.host}</span>{' '}
                        <span className="adm-muted">({n.role})</span>
                        {n.stale && (
                          <div>
                            <Badge tone="warn">
                              No report since {when(new Date(n.at * 1000).toISOString())}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td>
                        {n.spamhaus === 'dqs' ? (
                          <Badge tone="ok">DQS</Badge>
                        ) : n.spamhaus === 'public' ? (
                          <Badge tone="warn">Public mirrors</Badge>
                        ) : (
                          <Badge tone="neutral">Off</Badge>
                        )}
                      </td>
                      <td>{n.dnsbls}</td>
                      <td>{on(n.phishtank)}</td>
                      <td>{on(n.fuzzy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {nodes.some((n) => n.spamhaus === 'public') && (
                <p className="adm-note">
                  Spamhaus keeps its public mirrors for low-volume, non-commercial use. For a
                  commercial service, get a Data Query Service key from Spamhaus and set
                  SPAMHAUS_DQS_KEY in node.env on each mail server; the filter switches to it at its
                  next start.
                </p>
              )}
            </>
          )
        }
      </Loaded>
    </section>
  );
}
